export const PERIOD_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "thisweek", label: "This week" },
  { key: "thismonth", label: "This month" },
  { key: "lastmonth", label: "Last month" },
  { key: "thisyear", label: "This year" },
  { key: "lastyear", label: "Last year" },
  { key: "selectday", label: "Select day" },
  { key: "selectmonth", label: "Select month" },
  { key: "selectyear", label: "Select year" },
];

function addDays(date, amount) {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
}

function startOfDay(date) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function endOfDay(date) {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

function startOfMonth(year, month) {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

function endOfMonth(year, month) {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

function startOfYear(year) {
  return new Date(year, 0, 1, 0, 0, 0, 0);
}

function endOfYear(year) {
  return new Date(year, 11, 31, 23, 59, 59, 999);
}

function startOfWeek(date, weekStartsOn) {
  const result = startOfDay(date);
  const day = result.getDay();

  const daysSinceStart =
    weekStartsOn === "sunday" ? day : day === 0 ? 6 : day - 1;

  return addDays(result, -daysSinceStart);
}

function endOfWeek(date, weekStartsOn) {
  return endOfDay(addDays(startOfWeek(date, weekStartsOn), 6));
}

function parseInputDate(value) {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function parseInputMonth(value) {
  if (!value) return null;

  const [year, month] = value.split("-").map(Number);

  if (!year || !month) return null;

  return {
    year,
    month: month - 1,
  };
}

export function getPeriodRange({
  periodKey,
  selectedDay,
  selectedMonth,
  selectedYear,
  weekStartsOn = "monday",
}) {
  const now = new Date();

  switch (periodKey) {
    case "today":
      return {
        start: startOfDay(now),
        end: endOfDay(now),
      };

    case "thisweek":
      return {
        start: startOfWeek(now, weekStartsOn),
        end: endOfWeek(now, weekStartsOn),
      };

    case "thismonth":
      return {
        start: startOfMonth(now.getFullYear(), now.getMonth()),
        end: endOfMonth(now.getFullYear(), now.getMonth()),
      };

    case "lastmonth": {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      return {
        start: startOfMonth(
          lastMonth.getFullYear(),
          lastMonth.getMonth()
        ),
        end: endOfMonth(
          lastMonth.getFullYear(),
          lastMonth.getMonth()
        ),
      };
    }

    case "thisyear":
      return {
        start: startOfYear(now.getFullYear()),
        end: endOfYear(now.getFullYear()),
      };

    case "lastyear":
      return {
        start: startOfYear(now.getFullYear() - 1),
        end: endOfYear(now.getFullYear() - 1),
      };

    case "selectday": {
      const date = parseInputDate(selectedDay) || now;

      return {
        start: startOfDay(date),
        end: endOfDay(date),
      };
    }

    case "selectmonth": {
      const value = parseInputMonth(selectedMonth);

      if (!value) {
        return {
          start: startOfMonth(now.getFullYear(), now.getMonth()),
          end: endOfMonth(now.getFullYear(), now.getMonth()),
        };
      }

      return {
        start: startOfMonth(value.year, value.month),
        end: endOfMonth(value.year, value.month),
      };
    }

    case "selectyear": {
      const year = Number(selectedYear) || now.getFullYear();

      return {
        start: startOfYear(year),
        end: endOfYear(year),
      };
    }

    default:
      return {
        start: startOfMonth(now.getFullYear(), now.getMonth()),
        end: endOfMonth(now.getFullYear(), now.getMonth()),
      };
  }
}

export function getDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function getMonthInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

export function formatDocumentDate(value) {
  if (!value) return "";

  let date;

  if (typeof value.toDate === "function") {
    date = value.toDate();
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString();
}

export function formatCurrencyAmount(currencyCode = "USD", amount = 0) {
  const code = String(currencyCode || "USD").toUpperCase();
  const numericAmount = Number(amount || 0);

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return `${code} ${numericAmount.toFixed(2)}`;
  }
}

export function formatCurrencyTotals(totals = {}) {
  const entries = Object.entries(totals).filter(
    ([, amount]) => Number(amount || 0) !== 0
  );

  if (!entries.length) {
    return formatCurrencyAmount("USD", 0);
  }

  return entries
    .map(([currencyCode, amount]) =>
      formatCurrencyAmount(currencyCode, amount)
    )
    .join(", ");
}

export function addCurrencyMaps(mapA = {}, mapB = {}) {
  const result = {};
  const currencies = new Set([
    ...Object.keys(mapA),
    ...Object.keys(mapB),
  ]);

  currencies.forEach((currencyCode) => {
    result[currencyCode] =
      Number(mapA[currencyCode] || 0) +
      Number(mapB[currencyCode] || 0);
  });

  return result;
}

export function subtractCurrencyMaps(mapA = {}, mapB = {}) {
  const result = {};
  const currencies = new Set([
    ...Object.keys(mapA),
    ...Object.keys(mapB),
  ]);

  currencies.forEach((currencyCode) => {
    result[currencyCode] =
      Number(mapA[currencyCode] || 0) -
      Number(mapB[currencyCode] || 0);
  });

  return result;
}